import { useCallback, useEffect, useState } from 'react'
import type { BandColour, RangeRowView } from '../../shared/types'
import { api, type BandPatch, type ReferenceResponse } from '../api'
import { Card, ErrorNote, Pill, Spinner } from '../components'
import { useSession } from '../session-context'

const COLOURS: BandColour[] = ['teal', 'green', 'amber', 'red', 'grey']
const COLOUR_HINT: Record<BandColour, string> = { teal: 'Optimal', green: 'Normal', amber: 'Borderline', red: 'Action', grey: 'Neutral' }

/**
 * Clinic51's own ranges. Every marker is banded against these, never the
 * lab's interval. Admins edit in place; every change is logged. A band is
 * [lower, upper): the lower bound counts, the upper belongs to the next band.
 */
export function RangesScreen() {
  const { can } = useSession()
  const [ref, setRef] = useState<ReferenceResponse | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState<number | string | null>(null)
  const load = useCallback(async () => setRef(await api.reference('prevent51')), [])
  useEffect(() => { load().catch(setError) }, [load])
  if (ref === null) return error === null ? <Spinner /> : <ErrorNote error={error} />
  const editable = can('admin')
  const unconfirmed = ref.ranges.filter((r) => !r.confirmed).length

  const run = async (key: number | string, fn: () => Promise<unknown>): Promise<void> => {
    setBusy(key); setError(null)
    try { await fn(); await load() } catch (err) { setError(err) } finally { setBusy(null) }
  }

  return (
    <>
      <Card title="Reference ranges" subtitle="Clinic51's own bands, used for every result and every report. The lab's range is shown for reference only.">
        <ErrorNote error={error} />
        <p>
          {unconfirmed > 0 ? <><Pill tone="warn">{unconfirmed} bands not yet confirmed</Pill> Tick <b>Confirmed</b> on each band once the clinical lead has checked it.</> : <Pill tone="ok">All bands confirmed</Pill>}
        </p>
        <p className="note">
          A band runs from its lower bound up to, but not including, its upper bound; leave a bound blank for open-ended. <b>Target</b> marks the band we want patients in.
          Markers with a <b>female</b> set use that set for every patient; the <b>any</b> set is the fallback. {editable ? 'Changes save when you leave the box and are logged with your name.' : 'Only an admin can edit.'}
        </p>
      </Card>
      {ref.sections.map((s) => (
        <Card key={s.code} title={s.title} subtitle={s.parentTitle ?? undefined}>
          {ref.markers.filter((m) => m.sectionCode === s.code).map((m) => {
            const rows = ref.ranges.filter((r) => r.markerCode === m.code)
            const sets = Array.from(new Set(rows.map((r) => r.sex)))
            return (
              <div key={m.code} className="section-block">
                <h3>{m.name} <span className="muted small">{m.unit}{m.isDerived ? ' · calculated' : ''}</span>
                  {m.valueKind === 'numeric' && <label className="check key-pick"><input type="checkbox" checked={m.isKey === true} disabled={!editable || busy !== null} onChange={(e) => void run(`key-${m.code}`, () => api.setKeyMarker(m.code, e.target.checked))} /> key marker</label>}</h3>
                {rows.length === 0 && <p className="muted small">No bands: results are shown without a colour, and a clinician can name a band by hand on the assessment.</p>}
                {sets.map((sex) => (
                  <table key={sex} className="ranges">
                    <thead><tr><th>{sex === 'any' ? 'All patients' : 'Female'}</th><th>Shown as</th><th>Lower</th><th>Upper</th><th>Colour</th><th>Target</th><th>Confirmed</th><th>Note</th><th></th></tr></thead>
                    <tbody>
                      {rows.filter((r) => r.sex === sex).sort((a, b) => a.sort - b.sort).map((r) => (
                        <BandRow key={r.id} r={r} editable={editable} busy={busy === r.id} onSave={(patch) => void run(r.id, () => api.updateBand(r.id, patch))}
                          onDelete={() => { if (window.confirm(`Remove the "${r.label}" band from ${m.name}?`)) void run(r.id, () => api.deleteBand(r.id)) }} />
                      ))}
                    </tbody>
                  </table>
                ))}
                {editable && (
                  <p>
                    <button type="button" className="quiet" disabled={busy !== null} onClick={() => void run(`add-${m.code}`, () => api.addBand(m.code, sets.includes('female') ? 'female' : 'any', { label: 'New band', display: 'New band', colour: 'grey', lower: null, upper: null }))}>add band</button>
                    {!sets.includes('female') && <button type="button" className="quiet" disabled={busy !== null} onClick={() => void run(`addf-${m.code}`, () => api.addBand(m.code, 'female', { label: 'New band', display: 'New band', colour: 'grey', lower: null, upper: null }))}>add a female-specific set</button>}
                  </p>
                )}
              </div>
            )
          })}
        </Card>
      ))}
    </>
  )
}

function BandRow({ r, editable, busy, onSave, onDelete }: { r: RangeRowView; editable: boolean; busy: boolean; onSave: (p: BandPatch) => void; onDelete: () => void }) {
  const num = (v: string): number | null => (v.trim() === '' ? null : Number(v))
  const text = (key: 'label' | 'display' | 'note', value: string) => { if ((r[key] ?? '') !== value) onSave({ [key]: key === 'note' && value === '' ? null : value }) }
  if (!editable) {
    return (
      <tr>
        <td><span className={`chip ${r.colour}`}>{r.label}</span></td><td>{r.display}</td><td>{r.lower ?? '—'}</td><td>{r.upper ?? '—'}</td>
        <td className="muted small">{COLOUR_HINT[r.colour]}</td><td>{r.isTarget ? '✓' : ''}</td><td>{r.confirmed ? '✓' : <span className="muted">no</span>}</td><td className="muted small">{r.note}</td><td />
      </tr>
    )
  }
  return (
    <tr className={busy ? 'muted' : undefined}>
      <td><input defaultValue={r.label} onBlur={(e) => text('label', e.target.value)} style={{ width: '9em' }} /></td>
      <td><input defaultValue={r.display} onBlur={(e) => text('display', e.target.value)} style={{ width: '15em' }} /></td>
      <td><input defaultValue={r.lower ?? ''} inputMode="decimal" onBlur={(e) => { const v = num(e.target.value); if (v !== r.lower && !(v !== null && Number.isNaN(v))) onSave({ lower: v }) }} style={{ width: '5em' }} /></td>
      <td><input defaultValue={r.upper ?? ''} inputMode="decimal" onBlur={(e) => { const v = num(e.target.value); if (v !== r.upper && !(v !== null && Number.isNaN(v))) onSave({ upper: v }) }} style={{ width: '5em' }} /></td>
      <td><select value={r.colour} onChange={(e) => onSave({ colour: e.target.value as BandColour })}>{COLOURS.map((c) => <option key={c} value={c}>{c} · {COLOUR_HINT[c]}</option>)}</select></td>
      <td><input type="checkbox" checked={r.isTarget} onChange={(e) => onSave({ isTarget: e.target.checked })} /></td>
      <td><input type="checkbox" checked={r.confirmed} onChange={(e) => onSave({ confirmed: e.target.checked })} /></td>
      <td><input defaultValue={r.note ?? ''} placeholder="why" onBlur={(e) => text('note', e.target.value)} style={{ width: '14em' }} /></td>
      <td><button type="button" className="quiet" onClick={onDelete}>remove</button></td>
    </tr>
  )
}
